import React, { PureComponent } from 'react'
import Select, { components } from 'react-select'
import PropTypes from 'prop-types'
import { Button } from '../../components'
import { Scrollbars } from 'react-custom-scrollbars'
import { renderThumb, renderTrackVertical } from '../../resources/theme/q4.custom-scrollbar'
import { getClassName, THEMES } from '../../utils'

import './autocomplete.component.scss'

const ControlComponent = (props) => {
  const { error } = props.selectProps

  return (
    <components.Control {...props} className={`${(error && error.isError) ? 'autocomplete__control--error' : ''}`} />
  )
}

const ClearIndicator = (props) => {
  return (
    <components.ClearIndicator {...props}>
      <i className='autocomplete__indicator-clear q4i-close-4pt' />
    </components.ClearIndicator>
  )
}

const DropdownIndicator = (props) => {
  const { showDropdownIndicator } = props.selectProps

  return (
    showDropdownIndicator
      ? <components.DropdownIndicator {...props}>
        <i className='autocomplete__indicator-dropdown q4i-arrow-sm-down-4pt' />
      </components.DropdownIndicator> : null
  )
}

const LoadingIndicator = (props) => {
  const { isLoading } = props.selectProps

  return (
    isLoading
      ? <div className='autocomplete__indicator-loading'>
        <i className='autocomplete__indicator-loading-spinner' />
      </div> : null
  )
}

const Menu = (props) => {
  const { theme, size } = props.selectProps

  const baseClassName = getClassName('autocomplete_menu', [
    { condition: theme, trueClassName: `autocomplete_menu--${theme}` },
    { condition: size, trueClassName: `autocomplete_menu--${size}` }
  ])

  return (
    <components.Menu className={baseClassName} {...props} >
      {props.children}
    </components.Menu>)
}

const MenuList = (props) => {
  const { maxMenuHeight } = props.selectProps
  return (
    <components.MenuList {...props}>
      <Scrollbars
        className='react-scrollbar'
        autoHide
        autoHeight
        autoHeightMin={0}
        autoHeightMax={maxMenuHeight}
        hideTracksWhenNotNeeded
        renderThumbVertical={renderThumb}
        renderTrackVertical={renderTrackVertical}>
        {props.children}
      </Scrollbars>
    </components.MenuList>
  )
}

const Option = (props) => {
  const { data, isMulti, selectProps } = props
  const { isDefaultList, onMenuClose } = selectProps

  return (
    (data && data.type === 'action') ? <components.Option className='autocomplete__option--action' {...props} >
      <Button
        {...data}
        onClick={() => {
          const { onClick } = data
          // Close menu when an action item is selected
          // Otherwise, menu will remain open when multi-select is enabled
          onClick && onClick()
          onMenuClose && onMenuClose()
        }} />
    </components.Option> : isMulti && isDefaultList
      ? <div className='autocomplete__option--multi'>
        <components.Option {...props} />
      </div>
      : <components.Option {...props} />
  )
}

const ValueContainer = ({ children, ...props }) => {
  const { isMulti, selectProps } = props
  const {menuIsOpen, isSearchable, placeholder} = selectProps

  return (
    <components.ValueContainer {...props}>
      {isMulti
        ? <>
          {isSearchable ? (!menuIsOpen && placeholder) : placeholder}
          {React.cloneElement(children[1])}
        </>
        : children
      }
    </components.ValueContainer>
  )
}

const MultiValueContainer = () => {
  return null
}

/**
 * Autocomplete Component
 */
class AutoComplete extends PureComponent {

  /**
   * Filter option (one at a time) from options list using query from search
   * @param option
   * @param query
   * @returns {*}
   */
  filterOption = (option, query) => {
    if (!option) {
      return false
    }

    const label = option.label || ''
    const type = option.data && option.data.type

    // ensure that actions always show up in the results, regardless of user provided filterOption
    if (query && type === 'action') {
      return true
    }

    const { filterOption } = this.props

    // if filterOption is provided, use that
    if (filterOption) {
      return filterOption(option, query)
    }

    // otherwise, use default functionality
    return !query || label.toLowerCase().includes(query.toLowerCase())
  }

  /**
   * Render AutoSuggest Component
   * @returns {XML}
   */
  render () {
    const {
      className, theme, size, name, placeholder, value, defaultValue, options, onInputChange, onChange,
      onFocus, isLocal, error, loading, clearable, disabled, searchable, showDropdownIndicator,
      itemHeight, maxHeight, minHeight, limit, isMulti, isDefaultList, hideSelectedOptions, closeMenuOnSelect,
      portal
    } = this.props

    const target = portal ? document.body : null

    return (
      <Select
        name={name}
        theme={theme}
        size={size}
        className={getClassName('autocomplete-component', [
          { condition: className, trueClassName: className },
          { condition: theme, trueClassName: `autocomplete-component--${theme}` },
          { condition: size, trueClassName: `autocomplete-component--${size}` }
        ])}
        classNamePrefix='autocomplete'
        styles={{
          menu: base => ({ ...base, marginTop: 0 }),
          menuPortal: (base) => ({ ...base, zIndex: 9999 })
        }}
        // menuIsOpen
        menuPortalTarget={target}
        placeholder={placeholder}
        value={value}
        defaultValue={defaultValue}
        onInputChange={onInputChange}
        onFocus={onFocus}
        options={options}
        filterOption={isLocal ? this.filterOption : null}
        noOptionsMessage={() => null}
        onChange={onChange}
        error={error}
        isLoading={loading}
        isClearable={clearable}
        isDisabled={disabled}
        isSearchable={searchable}
        showDropdownIndicator={showDropdownIndicator}
        minMenuHeight={minHeight}
        maxMenuHeight={maxHeight}
        itemHeight={itemHeight}
        limit={limit}
        isMulti={isMulti}
        isDefaultList={isDefaultList}
        closeMenuOnSelect={closeMenuOnSelect}
        hideSelectedOptions={hideSelectedOptions}
        components={{
          Control: ControlComponent,
          ClearIndicator,
          DropdownIndicator,
          LoadingIndicator,
          Menu,
          MenuList,
          Option,
          ValueContainer,
          MultiValueContainer
        }}
      />
    )
  }
}

/**
 * Docs: https://github.com/JedWatson/react-select
 */
AutoComplete.propTypes = {
  className: PropTypes.string,
  theme: PropTypes.oneOf([THEMES.RAIN, THEMES.STEEL, THEMES.Q4_BLUE, THEMES.INK, THEMES.LIGHT_GREY, THEMES.WHITES]),
  size: PropTypes.string,
  name: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  defaultValue: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  options: PropTypes.array.isRequired,
  filterOption: PropTypes.func,
  onFocus: PropTypes.func,
  onInputChange: PropTypes.func,
  onChange: PropTypes.func,
  error: PropTypes.shape({
    isError: PropTypes.bool,
    message: PropTypes.string
  }),
  loading: PropTypes.bool,
  clearable: PropTypes.bool,
  disabled: PropTypes.bool,
  searchable: PropTypes.bool,
  showDropdownIndicator: PropTypes.bool,
  minHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  maxHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  itemHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  limit: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isMulti: PropTypes.bool,
  closeMenuOnSelect: PropTypes.bool,
  hideSelectedOptions: PropTypes.bool,
  /**
   * Used to show checkboxes in multiselect options (predefined list)
   */
  isDefaultList: PropTypes.bool,
  /**
   * Used to determine whether or not the results for this container are provided by the API
   * If true, a default AutoComplete.options.label filtering will be used unless AutoComplete.filterOption is provided
   */
  isLocal: PropTypes.bool,
  portal: PropTypes.bool
}

AutoComplete.defaultProps = {
  theme: THEMES.LIGHT_GREY,
  placeholder: '',
  options: [],
  minHeight: 0,
  maxHeight: 240,
  itemHeight: 40,
  limit: 6,
  isLocal: true,
  loading: false,
  clearable: true,
  disabled: false,
  showDropdownIndicator: true,
  isMulti: false,
  hideSelectedOptions: false,
  isDefaultList: false,
  portal: false
}

export default AutoComplete
